import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type {
  CreateTenantRequest,
  Tenant,
  TenantResponse,
  TenantsListResponse,
} from "@coco-mailrelay/shared-types";
import { apiRequest } from "../lib/apiRequest";
import { errorMessage } from "../lib/errors";

const { Title } = Typography;

// Mirror of the server's per-kind whitelist validation (apps/api admin/whitelist-value.ts)
// so a kind/value mismatch surfaces inline instead of as a 400 on submit.
const DOMAIN_RE =
  /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;
const EMAIL_RE = new RegExp(`^[^\\s@]+@${DOMAIN_RE.source.slice(1)}`);

function isValidWhitelistValue(kind: "address" | "domain", value: string): boolean {
  return (kind === "domain" ? DOMAIN_RE : EMAIL_RE).test(value);
}

interface CreateForm {
  slug: string;
  name: string;
  default_from: string;
  default_priority?: "high" | "normal";
  quota_per_min?: number;
  whitelist_kind?: "address" | "domain";
  whitelist_value?: string;
}

export default function TenantsPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm<CreateForm>();

  const { data, isLoading } = useQuery({
    queryKey: ["tenants"],
    queryFn: () => apiRequest<TenantsListResponse>("/tenants"),
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateTenantRequest) =>
      apiRequest<TenantResponse>("/tenants", { method: "POST", body }),
    onSuccess: (res) => {
      message.success(`Tenant "${res.tenant.slug}" created`);
      setModalOpen(false);
      form.resetFields();
      void queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
    onError: (err) => message.error(errorMessage(err, "Failed to create tenant")),
  });

  const handleCreate = (values: CreateForm): void => {
    const body: CreateTenantRequest = {
      slug: values.slug,
      name: values.name,
      default_from: values.default_from,
    };
    if (values.default_priority) body.default_priority = values.default_priority;
    if (values.quota_per_min !== undefined) body.quota_per_min = values.quota_per_min;
    if (values.whitelist_value) {
      body.whitelist = [{ kind: values.whitelist_kind ?? "address", value: values.whitelist_value }];
    }
    createMutation.mutate(body);
  };

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Tenants {data ? `(${data.total})` : ""}
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          Create tenant
        </Button>
      </div>

      <Table<Tenant>
        rowKey="id"
        loading={isLoading}
        dataSource={data?.tenants ?? []}
        pagination={false}
        onRow={(record) => ({ onClick: () => navigate(`/tenants/${record.id}`), style: { cursor: "pointer" } })}
        columns={[
          { title: "Slug", dataIndex: "slug", key: "slug" },
          { title: "Name", dataIndex: "name", key: "name" },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (s: Tenant["status"]) => (
              <Tag color={s === "active" ? "green" : "red"}>{s}</Tag>
            ),
          },
          { title: "default_from", dataIndex: "default_from", key: "default_from" },
          { title: "Quota/min", dataIndex: "quota_per_min", key: "quota_per_min" },
        ]}
      />

      <Modal
        title="Create tenant"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText="Create"
        confirmLoading={createMutation.isPending}
        destroyOnClose
      >
        <Form<CreateForm> form={form} layout="vertical" onFinish={handleCreate} preserve={false}>
          <Form.Item
            name="slug"
            label="Slug"
            tooltip="Permanent unique identifier used in URLs and internal references."
            extra="Lowercase letters, digits, and hyphens only (1–64 chars). Must be unique; cannot be changed after creation."
            rules={[
              { required: true, message: "Slug is required" },
              { pattern: /^[a-z0-9-]+$/, message: "Lowercase letters, digits, hyphens only" },
            ]}
          >
            <Input placeholder="acme" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            extra="Human-readable display name (1–200 chars)."
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="Acme Inc." />
          </Form.Item>
          <Form.Item
            name="default_from"
            label="Default from"
            tooltip="The default sender address for this tenant's emails."
            extra="Valid email address. Must be covered by the from-whitelist below — if you leave the whitelist blank, this exact address is whitelisted automatically (least privilege)."
            rules={[
              { required: true, message: "default_from is required" },
              { type: "email", message: "Must be a valid email" },
            ]}
          >
            <Input placeholder="noreply@acme.com" />
          </Form.Item>
          <Form.Item
            name="default_priority"
            label="Default priority"
            extra="Optional. Defaults to normal."
          >
            <Select
              allowClear
              placeholder="normal (default)"
              options={[
                { value: "high", label: "high" },
                { value: "normal", label: "normal" },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="quota_per_min"
            label="Quota per minute"
            extra="Optional. Max emails per minute (1–1,000,000). Defaults to 3000."
          >
            <InputNumber min={1} max={1_000_000} style={{ width: "100%" }} placeholder="3000 (default)" />
          </Form.Item>
          <Form.Item
            label="First whitelist entry"
            tooltip="Optional. Restricts which from-addresses this tenant may send as. Defaults to 'address' (least privilege); choose 'domain' only to allow any local-part on that domain."
            extra="Optional. Must cover default_from (same domain or exact address), otherwise creation fails. Leave blank to auto-whitelist default_from as an exact address (least privilege); pick 'domain' to allow the whole domain."
            style={{ marginBottom: 0 }}
          >
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item name="whitelist_kind" noStyle initialValue="address">
                <Select
                  style={{ width: 130 }}
                  options={[
                    { value: "address", label: "address" },
                    { value: "domain", label: "domain" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="whitelist_value"
                noStyle
                dependencies={["whitelist_kind"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value: string | undefined) {
                      if (!value) return Promise.resolve(); // optional
                      const kind = (getFieldValue("whitelist_kind") ?? "address") as "address" | "domain";
                      if (isValidWhitelistValue(kind, value.trim().toLowerCase())) return Promise.resolve();
                      return Promise.reject(
                        new Error(
                          kind === "domain"
                            ? "Enter a bare domain (e.g. acme.com), not an email address"
                            : "Enter a full email address (e.g. noreply@acme.com)",
                        ),
                      );
                    },
                  }),
                ]}
              >
                <Input placeholder="noreply@acme.com (blank → default_from added as exact address)" />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
